import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { alertsResponseSchema, type Assignment, type IssuanceForm } from '@unit/shared';
import { useApi } from '../../lib/useApi.js';
import { ApiError } from '../../components/ApiError.js';
import { formatDate } from '../issuance/FormsPage.js';
import { StatusPill } from '../gear/GearCard.js';

/**
 * The commander's alerts (REQUIREMENTS §2.6): overdue mission gear, open
 * loss/damage reports, items in rotation, forms signed with a comment.
 * Read-only: each row links to where the action is taken.
 */
export function AlertsPage() {
  const { t, i18n } = useTranslation();
  const state = useApi('/alerts', alertsResponseSchema);

  if (state.kind === 'loading') return <p className="text-slate-500">{t('home.loading')}</p>;
  if (state.kind === 'error') return <ApiError code={state.code} requestId={state.requestId} />;

  const { overdue, reports, inRotation, signedWithComment, soldiers } = state.data;
  const soldier = (uid: string) => soldiers.find((s) => s.uid === uid);
  const total = overdue.length + reports.length + inRotation.length + signedWithComment.length;

  const GearRow = ({ a, extra }: { a: Assignment; extra?: string }) => {
    const s = soldier(a.soldierUid);
    return (
      <li className="flex flex-wrap items-start justify-between gap-2 py-2" data-testid="alert-row" data-id={a.id}>
        <div>
          <p className="font-medium text-slate-900">
            {i18n.language.startsWith('en') ? a.nameEn : a.nameHe}
            {a.serialNumber && <span className="latin-inline ms-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{a.serialNumber}</span>}
          </p>
          <p className="text-sm text-slate-600">
            {s?.name ?? a.soldierUid}
            {s?.unitName && <span className="text-slate-400"> · {s.unitName}</span>}
          </p>
          {extra && <p className="mt-0.5 text-sm text-slate-700">{extra}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
          <StatusPill status={a.status} />
          {a.dueDate && <span className="latin-inline">{formatDate(a.dueDate, i18n.language)}</span>}
          {a.teamId && (
            <Link to={`/org/${a.teamId ?? a.platoonId}/soldier/${a.soldierUid}`} className="text-slate-700 underline-offset-4 hover:underline">
              {t('reports.openSoldier')}
            </Link>
          )}
        </div>
      </li>
    );
  };

  const FormRow = ({ f }: { f: IssuanceForm }) => (
    <li className="flex flex-wrap items-start justify-between gap-2 py-2" data-testid="alert-row" data-id={f.id}>
      <div>
        <p className="font-medium text-slate-900">{f.soldierName}</p>
        <p className="text-sm text-slate-700">“{f.soldierComment}”</p>
      </div>
      <div className="flex flex-col items-end gap-1 text-xs text-slate-500">
        {f.signedAt && <span className="latin-inline">{formatDate(f.signedAt, i18n.language)}</span>}
        <Link to={`/forms/${f.id}`} className="text-slate-700 underline-offset-4 hover:underline">{t('alerts.openForm')}</Link>
      </div>
    </li>
  );

  const Section = ({ id, title, count, children }: { id: string; title: string; count: number; children: ReactNode }) => (
    <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200" data-testid={`alerts-${id}`} data-count={count}>
      <h3 className="font-semibold text-slate-900">
        {title} <span className="latin-inline ms-1 rounded-full bg-slate-100 px-2 text-xs text-slate-600">{count}</span>
      </h3>
      {count === 0 ? <p className="mt-2 text-sm text-slate-500">{t('alerts.none')}</p> : <ul className="mt-2 divide-y divide-slate-100">{children}</ul>}
    </section>
  );

  return (
    <div className="flex flex-col gap-4" data-testid="alerts-page">
      <h2 className="text-lg font-semibold text-slate-900">
        {t('alerts.title')} {total === 0 && <span className="text-sm font-normal text-slate-500">— {t('alerts.allClear')}</span>}
      </h2>
      <Section id="overdue" title={t('alerts.overdue')} count={overdue.length}>
        {overdue.map((a) => <GearRow key={a.id} a={a} />)}
      </Section>
      <Section id="reports" title={t('alerts.reports')} count={reports.length}>
        {reports.map((a) => <GearRow key={a.id} a={a} extra={a.report?.notes} />)}
      </Section>
      <Section id="rotation" title={t('alerts.inRotation')} count={inRotation.length}>
        {inRotation.map((a) => <GearRow key={a.id} a={a} />)}
      </Section>
      <Section id="comments" title={t('alerts.signedWithComment')} count={signedWithComment.length}>
        {signedWithComment.map((f) => <FormRow key={f.id} f={f} />)}
      </Section>
    </div>
  );
}
